import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import React, { useEffect, useMemo, useState } from "react";
import { MuiBox } from "../box/MuiBox";
import { MuiIconButton } from "../button/MuiButton";
import MuiChip, { MuiChipProps } from "../chip/MuiChip";
import MuiEllipseLabel from "../ellipseLabel/MuiEllipseLabel";
import { MuiTypography } from "../typography/MuiTypography";
import { ChipWrapper, StyledChipPopover } from "./MuiChipWithPopover.style";

interface MuiChipWithPopoverProps {
  headerLabel: string;
  gridRef: React.RefObject<HTMLElement>;
  minPopoverWidth?: number;
  isDialogTitleWithEllipsis?: boolean;
  closePopoverARIALabel: string;
  showDoubleLabel?: boolean; // Flag to control double label rendering
  chipDoubleLabelPattern?: string; // Pattern for double label rendering
}

const MuiChipWithPopover: React.FC<MuiChipWithPopoverProps & MuiChipProps> = ({
  data,
  handleDelete,
  id = "",
  isEditable = true,
  headerLabel,
  gridRef,
  minPopoverWidth = 320,
  countToWrap,
  isDialogTitleWithEllipsis = false,
  closePopoverARIALabel,
  showMoreARIALabel,
  removeChipNameARIALabel,
  showDoubleLabel = false,
  chipDoubleLabelPattern = "",
  infoMessage,
}) => {
  const [anchorElChip, setAnchorElChip] = useState<null | HTMLElement>(null);
  const [gridWidth, setGridWidth] = useState(0); // Store the width of the grid

  useEffect(() => {
    // Function to measure the width of the grid
    if (gridRef.current) {
      setGridWidth(gridRef.current.getBoundingClientRect().width);
    }
  }, [gridRef.current]);

  const handleChipOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElChip(event.currentTarget);
  };

  const handleChipClose = () => {
    setAnchorElChip(null);
  };

  const handleCloseIconClick = () => {
    setAnchorElChip(null);
  };

  const handleDeleteChip = (chipValue: number | string, openFlag: boolean) => {
    if (data.length > 0 && openFlag) {
      setAnchorElChip(document.getElementById(id));
    } else {
      setAnchorElChip(null); // Close the popover if no items are left
    }
    handleDelete?.(chipValue, openFlag);
  };

  const newData = useMemo(() => {
    if (showDoubleLabel && chipDoubleLabelPattern) {
      return data.map((item) => {
        if (item.label && item.additionalInfo) {
          return {
            ...item,
            label: chipDoubleLabelPattern
              .replace("label", item.label)
              .replace("additionalInfo", item.additionalInfo),
          };
        }
        return item;
      });
    }
    return data;
  }, [data]);

  return (
    <ChipWrapper>
      <MuiBox id={id} className="mui-chip-wrapper">
        <MuiChip
          data={newData}
          handleDelete={handleDeleteChip}
          handleExpand={handleChipOpen}
          id={id}
          isEditable={isEditable}
          countToWrap={countToWrap}
          showMoreARIALabel={showMoreARIALabel}
          removeChipNameARIALabel={removeChipNameARIALabel}
          infoMessage={infoMessage}
        />

        <StyledChipPopover
          open={Boolean(anchorElChip)}
          anchorEl={anchorElChip}
          onClose={handleChipClose}
          popoverWidth={gridWidth}
          minWidth={minPopoverWidth}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <MuiBox className="header">
            {isDialogTitleWithEllipsis ? (
              <MuiEllipseLabel
                labelTypoVariant="h2SemiBold"
                text={headerLabel}
                labelTypoClassName={`text-title max-width`}
              />
            ) : (
              <MuiTypography
                variant="h2SemiBold"
                component="h2"
                className="text-title"
              >
                {headerLabel}
              </MuiTypography>
            )}
            <MuiIconButton
              onClick={handleCloseIconClick}
              aria-label={closePopoverARIALabel}
            >
              <CloseRoundedIcon className="close-image" />
            </MuiIconButton>
          </MuiBox>
          <MuiBox className="content">
            <MuiChip
              data={newData}
              handleDelete={handleDeleteChip}
              isExpanded={true}
              isEditable={isEditable}
              showMoreARIALabel={showMoreARIALabel}
              removeChipNameARIALabel={removeChipNameARIALabel}
            />
          </MuiBox>
        </StyledChipPopover>
      </MuiBox>
    </ChipWrapper>
  );
};

export default MuiChipWithPopover;
